/**
 * Handlers for Supabase Realtime broadcast messages.
 * Each handler processes broadcasts from a specific timestamp table.
 */

import queryClient from '@/services/queryClient'

import {
  invalidateQueriesFromTimestamps,
  LastModification,
  scheduleNetworksListInvalidationPreservingScroll,
} from '../timestampInvalidation'
import { scheduleInvalidateQueries } from './coalescedInvalidation'
import { DIRECT_TABLE_BROADCASTS, TIMESTAMP_TABLES } from './realtimeConstants'

/**
 * Handle broadcasts for `last_networks_update_timestamp` table.
 * This is GLOBAL - affects the networks list regardless of which network is open.
 */
export function handleNetworksListBroadcast(
  record: Record<string, unknown>,
  operation?: string,
): void {
  const networkId = record.network_id as string
  if (!networkId) return

  // Update all cached lastModification queries
  queryClient.setQueriesData<LastModification>(
    { queryKey: ['lastModification'] },
    (oldData) => {
      if (!oldData) return oldData

      if (operation === 'DELETE') {
        return {
          ...oldData,
          networks: oldData.networks.filter((n) => n.networkId !== networkId),
        }
      }

      const updateTime = record.update_time as string
      if (!updateTime) return oldData

      const existingIndex = oldData.networks.findIndex(
        (n) => n.networkId === networkId,
      )
      const updatedNetworks =
        existingIndex >= 0
          ? oldData.networks.map((n) =>
              n.networkId === networkId ? { networkId, updateTime } : n,
            )
          : [...oldData.networks, { networkId, updateTime }]
      return { ...oldData, networks: updatedNetworks }
    },
  )

  // Invalidate every network-list-shaped query so anything visible
  // (Home page browser, header popover, compare dialog, owner-filter
  // dropdown) refetches when another user creates/renames/edits a
  // network.
  //
  // This is the handler the KBE-806 burst went through: one broadcast per
  // touched timestamp row, each refetching all four shapes. The `setQueriesData`
  // patch above still applies per broadcast -- only the refetches coalesce.
  for (const procedure of [
    'getNetworksOrderedByLastModification',
    'listNetworkNamesForLists',
    'listDistinctNetworkEditors',
  ]) {
    scheduleInvalidateQueries([['network', procedure]])
  }

  scheduleNetworksListInvalidationPreservingScroll()
  scheduleInvalidateQueries([['variableLineage', 'getLineage']])
}

/**
 * Handle broadcasts for `optimization_jobs` table.
 * This is GLOBAL - optimization jobs are not network-specific.
 * Broadcasts directly from the table (no timestamp table needed).
 */
export function handleOptimizationJobsBroadcast(): void {
  // A running job updates its row repeatedly, so these coalesce for the same
  // reason the network list does.
  for (const procedure of [
    'getOptimizationJobs',
    // Backs the status indicator.
    'getRunningOptimizationsForNetwork',
    // So new optimization results are fetched.
    'getLatestAnalysisForNetwork',
  ]) {
    scheduleInvalidateQueries([['optimization', procedure]])
  }
}

/**
 * Handle broadcasts for `last_network_update_timestamps` table.
 * This is NETWORK-SPECIFIC - only process if it's for the current network.
 */
export function handleNetworkTimestampsBroadcast(
  record: Record<string, unknown>,
  operation?: string,
): void {
  if (operation === 'DELETE') return

  const networkId = record.network_id as string
  if (!networkId) return

  // Only apply the update if we have a cached LastModification for this
  // network — i.e. the user is (or recently was) viewing it. Avoids the
  // stale window of the old currentNetworkId module global.
  const oldData = queryClient.getQueryData<LastModification>([
    'lastModification',
    networkId,
  ])
  if (!oldData) return

  const newTimestamps: LastModification = {
    ...oldData,
    networkId,
    network: record.network as string,
    variableState: record.variable_state as string,
    assumptionSet: record.assumption_set as string,
    assumption: record.assumption as string,
    requirement: record.requirement as string,
    history: record.history as string,
  }

  queryClient.setQueryData<LastModification>(
    ['lastModification', networkId],
    newTimestamps,
  )

  invalidateQueriesFromTimestamps(networkId, newTimestamps, oldData)
}

/**
 * Handle broadcasts for `last_views_update_timestamp` table.
 * This is NETWORK-SPECIFIC - only process if it's for the current network.
 */
export function handleViewsBroadcast(
  record: Record<string, unknown>,
  operation?: string,
): void {
  const networkId = record.network_id as string
  const viewId = record.view_id as string
  if (!networkId || !viewId) return

  const oldData = queryClient.getQueryData<LastModification>([
    'lastModification',
    networkId,
  ])
  if (!oldData) return

  let updatedViews: LastModification['views']

  if (operation === 'DELETE') {
    updatedViews = oldData.views.filter((v) => v.viewId !== viewId)
  } else {
    const updateTime = record.update_time as string
    if (!updateTime) return

    const existingIndex = oldData.views.findIndex((v) => v.viewId === viewId)
    updatedViews =
      existingIndex >= 0
        ? oldData.views.map((v) =>
            v.viewId === viewId ? { viewId, updateTime } : v,
          )
        : [...oldData.views, { viewId, updateTime }]
  }

  const newTimestamps: LastModification = {
    ...oldData,
    views: updatedViews,
  }

  queryClient.setQueryData<LastModification>(
    ['lastModification', networkId],
    newTimestamps,
  )

  invalidateQueriesFromTimestamps(networkId, newTimestamps, oldData, viewId)
}

export function invalidateNotificationQueries(): void {
  for (const procedure of ['list', 'unreadCount']) {
    scheduleInvalidateQueries([['notification', procedure]])
  }
}

/**
 * Handle broadcasts for the `notification` table.
 * Skips invalidation when the broadcast recipient is known and doesn't match
 * the current user's profile, avoiding unnecessary refetches for other users'
 * notifications on the global topic.
 */
export function handleNotificationBroadcast(
  record: Record<string, unknown>,
): void {
  const recipientId = record['recipient_id']
  if (typeof recipientId === 'string') {
    const cached = queryClient.getQueriesData<{ id: string | null }>({
      queryKey: [['profile', 'getMine']],
    })
    const currentProfileId = cached[0]?.[1]?.id
    if (currentProfileId && currentProfileId !== recipientId) return
  }

  invalidateNotificationQueries()

  // When a background assistant task (batch execution, optimization) completes,
  // invalidate the open thread so its new messages appear without a page refresh.
  if (record['entity_kind'] === 'assistant_task') {
    scheduleInvalidateQueries([['assistant', 'getThread']])
  }
}

/**
 * Handle broadcasts for the `feature_flag` / `feature_flag_user` tables.
 * Re-resolves the current user's flags and the admin list so a toggle in the
 * flags settings page propagates to every open client without a reload (KBE-741).
 */
export function handleFeatureFlagBroadcast(): void {
  for (const procedure of ['getMyFlags', 'listAll']) {
    scheduleInvalidateQueries([['featureFlag', procedure]])
  }
}

/**
 * Main broadcast handler - routes messages to the appropriate handler.
 */
export function createBroadcastHandler() {
  return (message: {
    payload?: {
      table?: string
      operation?: string
      record?: Record<string, unknown> | null
      old_record?: Record<string, unknown> | null
    }
  }) => {
    const { table, record, old_record, operation } = message.payload ?? {}
    // For DELETE operations, use old_record; for INSERT/UPDATE, use record
    const data = record ?? old_record
    if (!data) return

    switch (table) {
      case TIMESTAMP_TABLES.NETWORK_TIMESTAMPS:
        handleNetworkTimestampsBroadcast(data, operation)
        break
      case TIMESTAMP_TABLES.NETWORKS_LIST:
        handleNetworksListBroadcast(data, operation)
        break
      case DIRECT_TABLE_BROADCASTS.OPTIMIZATION_JOBS:
        handleOptimizationJobsBroadcast()
        break
      case DIRECT_TABLE_BROADCASTS.NOTIFICATION:
        handleNotificationBroadcast(data)
        break
      case TIMESTAMP_TABLES.VIEWS:
        handleViewsBroadcast(data, operation)
        break
      case DIRECT_TABLE_BROADCASTS.FEATURE_FLAG:
      case DIRECT_TABLE_BROADCASTS.FEATURE_FLAG_USER:
        handleFeatureFlagBroadcast()
        break
    }
  }
}
